export interface JwtPayload {
  sub?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Decodifica o payload de um JWT sem validar assinatura (uso apenas no cliente
 * para ler claims como `exp`). Retorna `null` se o token for inválido/malformado.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  try {
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * `true` se o token estiver ausente, malformado, sem `exp` ou já expirado.
 * Aceita uma margem opcional (em ms) para considerar o token expirado antes do
 * horário real (ex.: para renovar de forma proativa).
 */
export function isTokenExpired(token: string, skewMs = 0): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 - skewMs <= Date.now();
}

/**
 * Tolerância de relógio (at-22). O `exp` é conferido contra o relógio do
 * DISPOSITIVO, que pode estar adiantado em relação ao servidor: nesse caso o
 * cliente considera expirado um token que o backend ainda aceitaria e provoca
 * uma renovação (ou um gate de loader) desnecessária. 30s cobrem o desvio
 * típico sem mascarar uma expiração real — o interceptor do `http` continua
 * sendo a rede de segurança para qualquer 401 que escape.
 */
export const TOLERANCIA_RELOGIO_MS = 30 * 1000;

/**
 * Igual a `isTokenExpired`, porém só considera o token expirado depois de
 * `exp + TOLERANCIA_RELOGIO_MS`. Usar nas decisões de NAVEGAÇÃO (gate de
 * bootstrap, PrivateRoute); para agendar renovação proativa continue usando o
 * `exp` diretamente.
 */
export function isTokenExpiradoComTolerancia(token: string): boolean {
  return isTokenExpired(token, -TOLERANCIA_RELOGIO_MS);
}
